export interface Capability {
  extern: string;
  label: string;
  color: string;
  attenuations: { key: string; value: string }[];
}

export interface ShowcaseExample {
  id: string;
  tab: string;
  prelude?: string;
  code: string;
  annotation: string;
  capabilities: Capability[];
}

export const showcaseExamples: ShowcaseExample[] = [
  {
    id: "provide",
    tab: "Provide",
    prelude: `extern http_get  : {url: String} -> String ! {Http.Get};
extern db_insert : {key: String, value: String} -> Unit ! {Db.Insert};`,
    code: `let fetch_and_store url key =
  let data = http_get {url: url};
  db_insert {key: key, value: data};

// The agent can only call http_get and db_insert.
// No file access. No LLM. Nothing else exists.`,
    annotation:
      "The host decides which capabilities to expose. The agent can only call what it receives.",
    capabilities: [
      {
        extern: "http_get",
        label: "Http.Get",
        color: "#fb923c",
        attenuations: [
          { key: "domain", value: "api.example.com" },
        ],
      },
      {
        extern: "db_insert",
        label: "Db.Insert",
        color: "#34d399",
        attenuations: [
          { key: "table", value: "cache" },
        ],
      },
    ],
  },
  {
    id: "attenuate",
    tab: "Attenuate",
    prelude: `extern read_file : {path: String} -> String ! {File.Read};`,
    code: `// Attenuate: restrict to a single directory.
let config_read path = read_file {path: "/config/" ++ path};

// Attenuate further: a single file, no arguments.
let app_config _ = read_file {path: "/config/app.toml"};

// The agent receives app_config.
// It cannot read anything else. Not by cleverness,
// not by prompt injection \u2014 by construction.`,
    annotation:
      "The host narrows a broad capability into a scoped one via partial application. No special runtime \u2014 just closures.",
    capabilities: [
      {
        extern: "read_file",
        label: "File.Read",
        color: "#38bdf8",
        attenuations: [
          { key: "path", value: "/config/*" },
        ],
      },
      {
        extern: "app_config",
        label: "File.Read (attenuated)",
        color: "#7dd3fc",
        attenuations: [
          { key: "path", value: "/config/app.toml" },
        ],
      },
    ],
  },
  {
    id: "compose",
    tab: "Compose",
    prelude: `extern db_insert : {key: String, value: String} -> Unit ! {Db.Insert};`,
    code: `let classify text =
  model @{category: String, confidence: Float}
  \`Classify this text: {{ text }}\`;

let store label data =
  db_insert {key: label, value: toString data};

let classify_and_store text =
  match classify text with
  | Ok r -> store (.category r) r
  | Err e -> store "error" e;

// Uses two capabilities: model and db_insert.
// The type tracks both. No surprises.`,
    annotation:
      "When capabilities compose, their types merge. The signature always tells the full story.",
    capabilities: [
      {
        extern: "model",
        label: "LLM.Call",
        color: "#c084fc",
        attenuations: [
          { key: "provider", value: "gpt-4o" },
          { key: "max_tokens", value: "1024" },
        ],
      },
      {
        extern: "db_insert",
        label: "Db.Insert",
        color: "#34d399",
        attenuations: [
          { key: "table", value: "classifications" },
        ],
      },
    ],
  },
];

export function encodeCode(code: string): string {
  if (typeof Buffer !== "undefined") {
    return encodeURIComponent(Buffer.from(code, "utf-8").toString("base64"));
  }
  return encodeURIComponent(btoa(unescape(encodeURIComponent(code))));
}
